import React from 'react';
import { Tabs, Tab, Toolbar, AppBar, Box, Typography, ListItemIcon, ListItemText, MenuItem, useTheme } from '@mui/material';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { UserMenu, Logout, LoadingIndicator, useTranslate, usePermissions } from 'react-admin';
import { LocalesMenuButton } from 'react-admin';

// --- ICONE AGGIORNATE E PIÙ MODERNE ---
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // Più carino di Event
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';   // Specifico per PDF
import TableChartIcon from '@mui/icons-material/TableChart';       // Specifico per Tabelle/Pivot
import ApartmentIcon from '@mui/icons-material/Apartment';
import BadgeIcon from '@mui/icons-material/Badge';                 // Meglio di Groups per "Dipendenti"
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

const Header = () => {
    const location = useLocation();
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const navigate = useNavigate();
    const theme = useTheme();

    // Configurazione del Menu: Più facile da mantenere
    const NAV_ITEMS = [
        { 
            label: translate('ess.calendar.name'), 
            path: '/calendar', 
            icon: <CalendarMonthIcon /> 
        },
        { 
            label: translate('ess.tablesummary.name'), 
            path: '/table-summary', 
            icon: <TableChartIcon /> 
        },
        { 
            label: translate('ess.summary.name'), 
            path: '/pdf-summary', 
            icon: <PictureAsPdfIcon /> 
        },
        { 
            label: translate('resources.customer.name', { smart_count: 2 }), 
            path: '/customer', 
            icon: <ApartmentIcon /> 
        },
        { 
            label: translate('resources.employee.name', { smart_count: 2 }), 
            path: '/employee', 
            icon: <BadgeIcon /> 
        }
    ];

    // Logica per determinare la tab attiva
    let currentPath = '/';;
    // Cerca quale path matcha l'URL attuale
    const activeItem = NAV_ITEMS.find(item => !!matchPath(`${item.path}/*`, location.pathname));
    if (activeItem) {
        currentPath = activeItem.path;
    } else if (!!matchPath('/user/*', location.pathname)) {
        currentPath = '/user'; // Fallback per la gestione utenti se non è nelle tab
    }

    return (
        <AppBar position="static" sx={{ 
            backgroundColor: theme.palette.primary.main, 
            boxShadow: 3 // Un po' di ombreggiatura moderna
        }}>
            <Toolbar sx={{ minHeight: '64px' }}> {/* Altezza standard più ariosa */}
                
                <Box flex={1} display="flex" justifyContent="space-between" alignItems="center">
                    
                    {/* LOGO AREA */}
                    <Box display="flex" alignItems="center" sx={{ mr: 2 }}>
                        <img alt="Shift Scheduler" src='./logo.png' height="40" style={{ paddingRight: "15px" }} />
                        <Typography 
                            variant="h6" 
                            noWrap 
                            component="div" 
                            sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600 }} // Grassetto più moderno
                        >
                            Shift Scheduler
                        </Typography>
                    </Box>

                    {/* NAVIGATION TABS */}
                    <Box sx={{ flexGrow: 1, display: 'flex' }}>
                        <Tabs
                            value={currentPath}
                            aria-label="Navigation Tabs"
                            indicatorColor="secondary"
                            textColor="inherit"
                            sx={{
                                '& .MuiTabs-indicator': {
                                    height: '4px', // Indicatore più spesso (stile Material You)
                                    borderTopLeftRadius: '3px',
                                    borderTopRightRadius: '3px',
                                }
                            }}
                        >
                            {NAV_ITEMS.map((item) => (
                                <Tab
                                    key={item.path}
                                    label={item.label}
                                    value={item.path}
                                    component={Link}
                                    to={item.path}
                                    icon={item.icon}
                                    iconPosition="start"
                                    sx={{
                                        minHeight: '64px',
                                        fontWeight: currentPath === item.path ? 700 : 400, // Grassetto se attivo
                                        opacity: currentPath === item.path ? 1 : 0.8,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            backgroundColor: 'rgba(255,255,255, 0.1)' // Effetto hover
                                        }
                                    }}
                                />
                            ))}
                        </Tabs>
                    </Box>

                    {/* USER & SETTINGS AREA */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <LocalesMenuButton 
                            languages={[
                                { locale: 'en', name: 'EN' }, // Usa sigle corte per risparmiare spazio
                                { locale: 'it', name: 'IT' },
                            ]}
                        />
                        <LoadingIndicator sx={{ '& .RaLoadingIndicator-loader': { marginTop: 0 } }} />
                        
                        <UserMenu>
                            {permissions?.includes("ROLE_ADMIN") && (
                                <MenuItem onClick={() => navigate('/user')}>
                                    <ListItemIcon><GroupAddIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>Manage Users</ListItemText>
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => navigate('/user/preferences')}>
                                <ListItemIcon><ManageAccountsIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>{translate("ess.users.preferences.title")}</ListItemText>
                            </MenuItem>
                            <Logout />
                        </UserMenu>
                    </Box>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default Header;